<script lang="ts" setup>
  import { computed } from 'vue';

  const props = defineProps({
    selected: {
      type: Boolean,
      default: false,
      required: false,
    },
    interactive: {
      type: Boolean,
      default: false,
      required: false,
    },
  });

  const listCardClasses = computed(() => {
    return {
      'list-card--selected': props.selected,
      'list-card--interactive': props.interactive,
    };
  });

  const specificProps = computed(() => ({
    tabindex: props.interactive ? 0 : null,
    role: props.interactive ? 'button' : undefined,
  }));

  const component = computed(() => {
    return props.interactive ? 'div' : 'article';
  });
</script>

<template>
  <component :is="component" :class="{ ...listCardClasses}" class="list-card" v-bind="{ ...specificProps }">
    <div class="list-card__wrapper">
      <div v-if="$slots.image" class="list-card__image">
        <slot name="image" />
      </div>
      <div class="list-card__content">
        <div class="list-card__content-default">
          <slot name="default" />
        </div>
        <div v-if="$slots.icons || $slots.actions" class="list-card__actions">
          <div v-if="$slots.icons" class="list-card__actions-icons">
            <slot name="icons" />
          </div>
          <div v-if="$slots.actions" class="list-card__actions-content">
            <slot name="actions" />
          </div>
        </div>
      </div>
    </div>
  </component>
</template>

<style lang="scss">
  @use "@storefront/styles/mixins/media";

  :root {
    --sf-list-card-wrapper-direction: row;

    --sf-list-card-border-radius: var(--border-radius);
    --sf-list-card-gap: 1rem;
    --sf-list-card-padding-x: 1rem;
    --sf-list-card-padding-y: 1rem;

    --sf-list-card-color: var(--color-default);
    --sf-list-card-background: var(--background-default);
    --sf-list-card-border: 1px solid var(--secondary-border);
    --sf-list-card-box-shadow: none;

    --sf-list-card-color-alt: var(--color-secondary);
    --sf-list-card-color-alt-hover: var(--secondary-foreground-hover);
    --sf-list-card-color-alt-selected: var(--primary-foreground-active);

    --sf-list-card-color-hover: var(--secondary-foreground-hover);
    --sf-list-card-background-hover: var(--secondary-background-hover);
    --sf-list-card-border-hover: 1px solid var(--secondary-border-hover);
    --sf-list-card-box-shadow-hover: var(--secondary-box-shadow-200);

    --sf-list-card-color-selected: var(--primary-foreground-active);
    --sf-list-card-background-selected: var(--primary-background-active);
    --sf-list-card-border-selected: 1px solid var(--primary-border-active);
    --sf-list-card-box-shadow-selected: var(--primary-box-shadow-200);

    --sf-list-card-transition-timing-func: ease-out;
    --sf-list-card-transition-duration: 200ms;

    --sf-list-card-price-current-font-size: 1rem;
    --sf-list-card-price-current-font-line-height: 1rem;
    --sf-list-card-price-catalog-font-size: 0.75rem;
    --sf-list-card-price-catalog-font-line-height: 1rem;
  }

  .list-card {
    position: relative;
    flex-grow: 1;
    width: 100%;
    color: var(--sf-list-card-color);
    background: var(--sf-list-card-background);
    border: var(--sf-list-card-border);
    border-radius: var(--sf-list-card-border-radius);
    box-shadow: var(--sf-list-card-box-shadow);
    transition: border var(--sf-list-card-transition-duration) var(--sf-list-card-transition-timing-func),
    background var(--sf-list-card-transition-duration) var(--sf-list-card-transition-timing-func),
    box-shadow var(--sf-list-card-transition-duration) var(--sf-list-card-transition-timing-func),
    scale 0.1s ease;

    &--interactive {
      cursor: pointer;
    }

    &--interactive:not(.list-card--selected):hover,
    &--interactive:not(.list-card--selected):focus-visible {
      --sf-list-card-color: var(--sf-list-card-color-hover);
      --sf-list-card-color-alt: var(--sf-list-card-color-alt-hover);
      --sf-list-card-background: var(--sf-list-card-background-hover);
      --sf-list-card-border: var(--sf-list-card-border-hover);
      --sf-list-card-box-shadow: var(--sf-list-card-box-shadow-hover);
    }

    &--interactive:not(.list-card--selected):active {
      scale: 0.99;
    }

    &--selected {
      --sf-list-card-color: var(--sf-list-card-color-selected);
      --sf-list-card-color-alt: var(--sf-list-card-color-alt-selected);
      --sf-list-card-background: var(--sf-list-card-background-selected);
      --sf-list-card-border: var(--sf-list-card-border-selected);
      --sf-list-card-box-shadow: var(--sf-list-card-box-shadow-selected);
    }

    &__wrapper {
      display: flex;
      justify-content: flex-start;
      flex-direction: var(--sf-list-card-wrapper-direction);
      position: relative;
      padding: var(--sf-list-card-padding-y) var(--sf-list-card-padding-x);
      gap: var(--sf-list-card-gap);
    }

    &__content {
      display: flex;
      justify-content: space-between;
      align-items: stretch;
      flex: 1 1 auto;
      gap: var(--sf-list-card-gap);
    }

    &__content-default {
      display: flex;
      flex-direction: column;
      flex: 1 1 auto;
      color: var(--sf-list-card-color);
      gap: calc(var(--sf-list-card-gap) / 2);

      a {
        color: var(--sf-list-card-color);
        text-decoration: underline;
        text-decoration-color: transparent;
        transition: text-decoration-color var(--transition-primary-duration, 0.2s) var(--transition-primary-timing-function, ease-out);
      }

      @media(hover: hover) {
        a:hover {
          text-decoration-color: var(--sf-list-card-color);
        }
      }
    }

    &__image {
      flex: none;
    }

    &__actions {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      gap: var(--sf-list-card-gap);
    }

    &__actions-icons {
      display: flex;
      align-items: center;
      gap: calc(var(--sf-list-card-gap) / 2);
    }

    &__actions-content {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      gap: calc(var(--sf-list-card-gap) / 2);

      .omnibus {
        text-align: right;
      }
    }

    .price {
      --sf-product-price-current-font-size: var(--sf-list-card-price-current-font-size);
      --sf-product-price-current-font-line-height: var(--sf-list-card-price-current-font-line-height);
      --sf-product-price-catalog-font-size: var(--sf-list-card-price-catalog-font-size);
      --sf-product-price-catalog-font-line-height: var(--sf-list-card-price-catalog-font-line-height);
    }
  }

  @include media.query(sm) {
    .list-card {
      &__wrapper {
        gap: var(--sf-list-card-gap);
      }

      &__content {
        flex-direction: column;
      }

      &__actions {
        gap: 0;
      }

      &_actions-content {
        gap: 0;
      }
    }
  }
</style>
